import { PlanLevel, PlanName } from '@/features/common';

import { BillingPlan } from '../types';

export interface IBillingPlansDescription {
  plan: PlanName;
  planLevel: PlanLevel;
  planCode: {
    monthly: BillingPlan;
    annual: BillingPlan;
  };
  validPlanCodes?: BillingPlan[];
  description: string;
  price: {
    monthly: { perMonth: number };
    annual: { perMonth: number };
  };
  overview: string;
  limits: {
    limit: string;
    info?: string;
  }[];
}

interface IBillingPlansConfig {
  plans: IBillingPlansDescription[];
  yearlyPlansDiscountPercentage: number;
}

export const billingPlansDescriptions: IBillingPlansConfig = {
  yearlyPlansDiscountPercentage: 20,
  plans: [
    {
      plan: 'free',
      planLevel: 'free',
      planCode: { monthly: '2024-09-free-ats', annual: '2024-09-free-ats' },
      description:
        'Basic ATS and assessment features ideal for small businesses',
      price: {
        monthly: { perMonth: 0 },
        annual: { perMonth: 0 },
      },
      overview: 'Includes',
      limits: [
        { limit: '1 active job opening' },
        { limit: '1 skills test' },
        {
          limit: 'Candidates up to 30 days',
          info: 'Access candidate data for the past 30 days',
        },
      ],
    },
    {
      plan: 'starter',
      planLevel: 'starter',
      planCode: {
        monthly: '2024-09-starter-1m-ats',
        annual: '2024-09-starter-12m-ats',
      },
      description:
        'Customizable skills-first, full-cycle hiring software for growing teams',
      price: {
        monthly: { perMonth: 239 },
        annual: { perMonth: 199 },
      },
      overview: 'Includes',
      limits: [
        { limit: '3 active job openings' },
        { limit: 'Unlimited, customizable tests' },
        { limit: 'Unlimited candidates' },
      ],
    },
    {
      plan: 'premium',
      planLevel: 'premium',
      planCode: {
        monthly: '2025-04-premium-1m-ats',
        annual: '2025-04-premium-12m-ats',
      },
      description: 'Great for scaling teams with ongoing hiring needs',
      price: {
        monthly: { perMonth: 419 },
        annual: { perMonth: 349 },
      },
      overview: 'Everything in Starter +',
      limits: [
        { limit: '6 active job openings' },
        { limit: 'Workspace & job analytics' },
        { limit: 'Homework assignments' },
      ],
    },
    {
      plan: 'business',
      planLevel: 'business',
      planCode: {
        monthly: '2025-04-business-tier1-12m',
        annual: '2025-04-business-tier1-12m',
      },
      // Business plans are not available for in-app purchase.
      // Users will book a demo, and the sales team assigns the appropriate plan manually.
      // That's why we don't require specific plan names for business tiers.
      // // validPlanCodes: [ '2025-04-business-tier1-12m', '2025-04-business-tier2-12m', '2025-04-business-tier3-12m' ],
      description: 'Great for large teams with ongoing hiring needs',
      price: {
        monthly: { perMonth: 0 },
        annual: { perMonth: 0 },
      },
      overview: 'Everything in Premium +',
      limits: [
        { limit: '7+ active job openings' },
        { limit: 'Customer Success Manager' },
        {
          limit: 'Migration support',
          info: 'Onboarding support and data migration from other tools',
        },
      ],
    },
  ],
};

export const billingPlans: Partial<
  Record<
    BillingPlan,
    { name: string; type: 'Annually' | 'Monthly'; price: number | null }
  >
> = {
  '2024-09-starter-1m-ats': {
    name: 'Starter',
    type: 'Monthly',
    price: 239,
  },
  '2024-09-starter-12m-ats': {
    name: 'Starter',
    type: 'Annually',
    price: 199,
  },
  '2025-04-premium-1m-ats': {
    name: 'Premium',
    type: 'Monthly',
    price: 419,
  },
  '2025-04-premium-12m-ats': {
    name: 'Premium',
    type: 'Annually',
    price: 349,
  },
  '2025-04-business-tier1-12m': {
    name: 'Business',
    type: 'Annually',
    price: null,
  },
  '2025-04-business-tier2-12m': {
    name: 'Business',
    type: 'Annually',
    price: null,
  },
  '2025-04-business-tier3-12m': {
    name: 'Business',
    type: 'Annually',
    price: null,
  },
};
